import { useEffect, useRef } from 'react';
import * as mapboxgl from 'mapbox-gl';
import {
    MotionPoint,
    buildGlidePath,
    nearestEquivalentDeg,
    quantizeDeg,
    sampleByTime,
    segmentHeading,
    segmentMoves,
} from './deliveryMonitorMotion';

// 朝向量化到 10° 才写 DOM，避免每帧都改 transform 打断 1.2s 的转向过渡
const ROTATE_STEP_DEG = 10;

export interface GlideCar {
    marker: mapboxgl.Marker;
    /** 车身旋转层；朝向直接写它的 transform，不走 React 重渲染 */
    rotor: HTMLDivElement;
    /** 闪烁层；当前线段两点坐标不同时挂上 movingClass */
    pulser: HTMLElement;
}

interface CarMotion {
    car: GlideCar;
    /** 本轮滑行路径；[] = 停在 rest */
    path: MotionPoint[];
    t0: number;
    rest: MotionPoint;
    /** 已写入 DOM 的角度（连续值，不归一），null = 还没写过 */
    rotation: number | null;
    /** pulser 上是否已挂 movingClass */
    moving: boolean;
}

export interface CarGlide {
    /**
     * 每次轮询调用：已有的车从当前显示位置接着滑向 track 中更新的点；
     * 新出现的车直接落在 track 第一个点，再滑过其余点。没有新点就原地不动
     */
    place: (driverAccountId: string, car: GlideCar, track: MotionPoint[]) => void;
    remove: (driverAccountId: string) => void;
    dispose: () => void;
}

/**
 * 小车平滑移动：每轮轮询拿到的点在 durationMs 内按时间戳比例插值走完。
 * 用一个 rAF 循环直接调 marker.setLngLat；没有车在动时停掉循环。
 * 标签页切到后台时 rAF 暂停，切回来按当前时间算出的进度已到头，直接跳到最新位置。
 */
const createCarGlide = (durationMs: number, movingClass: string): CarGlide => {
    const motions = new Map<string, CarMotion>();
    let raf = 0;

    const rotate = (m: CarMotion, heading: number | null) => {
        if (heading === null) return;
        const deg = nearestEquivalentDeg(m.rotation, quantizeDeg(heading, ROTATE_STEP_DEG));
        if (deg === m.rotation) return;
        m.car.rotor.style.transform = `rotate(${deg}deg)`;
        m.rotation = deg;
    };

    // 只在状态变化时写 DOM，避免每帧重设 class 打断闪烁动画
    const setMoving = (m: CarMotion, moving: boolean) => {
        if (m.moving === moving) return;
        m.car.pulser.classList.toggle(movingClass, moving);
        m.moving = moving;
    };

    const shownPoint = (m: CarMotion, now: number): MotionPoint =>
        m.path.length === 0 ? m.rest : sampleByTime(m.path, (now - m.t0) / durationMs).point;

    const frame = (now: number) => {
        let moving = false;
        motions.forEach((m) => {
            if (m.path.length === 0) return;
            const progress = (now - m.t0) / durationMs;
            const s = sampleByTime(m.path, progress);
            m.car.marker.setLngLat([s.point.lng, s.point.lat]);
            rotate(m, segmentHeading(m.path, s.segment));
            setMoving(m, progress < 1 && segmentMoves(m.path, s.segment));
            if (progress >= 1) {
                m.rest = m.path[m.path.length - 1];
                m.path = [];
            } else {
                moving = true;
            }
        });
        raf = moving ? requestAnimationFrame(frame) : 0;
    };

    const place = (driverAccountId: string, car: GlideCar, track: MotionPoint[]) => {
        if (track.length === 0) return;
        const now = performance.now();
        let m = motions.get(driverAccountId);
        if (!m || m.car !== car) {
            const first = track[0];
            car.marker.setLngLat([first.lng, first.lat]);
            m = { car, path: [], t0: now, rest: first, rotation: null, moving: false };
            motions.set(driverAccountId, m);
            rotate(m, track[track.length - 1].bearing ?? 0);
        }
        const shown = shownPoint(m, now);
        const path = buildGlidePath(shown, track);
        m.rest = shown;
        m.path = path;
        m.t0 = now;
        // 没有新点不会再进 frame，在这里停掉闪烁；有新点交给 frame 按当前线段判断
        if (path.length === 0) setMoving(m, false);
        if (path.length > 0 && raf === 0) raf = requestAnimationFrame(frame);
    };

    return {
        place,
        remove: (driverAccountId) => {
            motions.delete(driverAccountId);
        },
        dispose: () => {
            cancelAnimationFrame(raf);
            raf = 0;
            motions.clear();
        },
    };
};

export const useCarGlide = (durationMs: number, movingClass: string): CarGlide => {
    const ref = useRef<CarGlide | null>(null);
    if (ref.current === null) ref.current = createCarGlide(durationMs, movingClass);
    useEffect(() => () => ref.current?.dispose(), []);
    return ref.current;
};
